import { uniqBy } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { USER_ROLES } from '@pwc/pwc-deals-web-common';
import { getUserRole } from 'common/redux/security/loggedInUser/loggedInUserSlice';

import { InfiniteListMessage } from '@pwc/pwc-deals-ui';
import { SyncedInfoProvider } from 'common/components/Autosync/SyncedInfoProvider.tsx';
import NoContentForTagsCard from '../../common/components/ContentActions/NoContentForTagsCard/NoContentForTagsCard';
import InfiniteListLoadIndicator from '../../common/components/InfiniteListLoadIndicator/InfiniteListLoadIndicator';
import {
  sortAscending,
  sortDescending,
} from '../../common/components/proptypes/commonProptypes';
import ResponsiveLoader from '../../common/components/ResponsiveLoader/ResponsiveLoader';
import useWindowSize from '../../common/hooks/useWindowSize';
import {
  getUserSettingsSelector,
  setUserSettings,
} from '../../common/redux/userSettings/userSettingsSlice';
import { selectAllTags } from '../analyses/analysisSlice';
import { sortKeys } from '../contentList/contentListSortKeys';
import EmptyHypothesesCard from './EmptyHypotheses/EmptyHypothesesCard';
import './HypothesesListContainer.scss';
import { deleteHypothesis } from './hypothesesService';
import { getPagedDataForGrid, getPagedDataForList } from './kfaService';
import HypotyhesesView from './HypotyhesesView';
import { loadLibraryItems, prepareLibraryItems } from './libraryItemsSupports';
import useIsMountedRef from '../../common/hooks/useIsMountedRef';
import useAllConnections from '../narratives/hooks/useAllConnections';
import LoadingError from '../../common/components/LoadingError';

const GRID_PAGE_SIZE = 3;
const LIST_PAGE_SIZE = 10;

const HypothesesListContainer = () => {
  const dispatch = useDispatch();
  const userSettings = useSelector(getUserSettingsSelector);
  const {
    settings: { viewGrid, darkMode },
  } = userSettings;
  const { projectId } = useParams();
  const [filterTags, setFilterTags] = useState([]);
  const [data, setData] = useState([]);
  const [isDeletePerformed, setIsDeletePerformed] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [isError, setIsError] = useState(false);
  const [currentPageInState, setCurrentPageInState] = useState(0);
  const [completeGridResponse, setCompleteGridResponse] = useState({
    libraryItemGroups: {
      excelPptLibraryItemList: [],
      richTextLibraryItemList: [],
      powerBiLibraryItemList: [],
    },
    items: [],
  });
  const [currentRowCount, setCurrentRowCount] = useState(null);
  const [currentSortKey, setCurrentSortKey] = useState(sortKeys.sortOrder);
  const [currentSortDir, setCurrentSortDir] = useState(sortAscending);
  const allTags = useSelector(selectAllTags);
  const userRole = useSelector(getUserRole);
  const isPwcUser = userRole === USER_ROLES.PWC_USER;
  const isMountedRef = useIsMountedRef();
  const [allPreparedItems, setAllPreparedItems] = useState({
    excelLibraryItems: [],
    pptLibraryItems: [],
    requests: [],
  });
  const { t } = useTranslation('');
  useEffect(() => {
    setCurrentSortKey(sortKeys.sortOrder);
    setCurrentSortDir(sortAscending);
    setCurrentRowCount(null);
    setCurrentPageInState(0);
    setData([]);
    setCompleteGridResponse({
      libraryItemGroups: {
        excelPptLibraryItemList: [],
        richTextLibraryItemList: [],
        powerBiLibraryItemList: [],
      },
      items: [],
    });
    setAllPreparedItems({
      excelLibraryItems: [],
      pptLibraryItems: [],
      requests: [],
    });
  }, [isPwcUser]);

  const { allConnections: allRetrievedConnections } = useAllConnections(
    isMountedRef.current
  );

  const { height } = useWindowSize();

  const canBeScrolled = useCallback(
    () => height && document.body.scrollHeight > height,
    [height]
  );

  const contextStatusInfoItem = {
    shareWithAll: false,
    isViewMode: true,
    isEditMode: false,
  };

  const toggleView = () => {
    setData([]);
    setCurrentPageInState(0);
    dispatch(
      setUserSettings({
        viewGrid: !viewGrid,
      })
    );
  };

  const onFilter = (tags) => {
    setFilterTags(
      allTags.filter((tag) => tags.includes(tag.text)).map((i) => i.id)
    );
    setData([]);
    setIsFetching(true);
    setCurrentPageInState(0);
  };

  const onDelete = async ({ contentId, contentThreadId, sortOrder }) => {
    const response = await deleteHypothesis(projectId, contentThreadId);

    if (response) {
      const currentData = data.filter(
        (item) =>
          item.contentThreadId !== contentThreadId &&
          item.contentId !== contentId
      );
      currentData.forEach((item) => {
        if (item.sortOrder > sortOrder) {
          item.sortOrder -= 1;
        }
      });
      setData(currentData);
      setIsDeletePerformed(true);
    }
  };

  const onReorder = (orderedData) => {
    setData(orderedData);
  };

  const fetchPagedDataForList = useCallback(() => {
    return getPagedDataForList(
      projectId,
      currentPageInState,
      LIST_PAGE_SIZE,
      currentSortKey,
      currentSortDir === sortDescending,
      filterTags.length ? filterTags : null,
      !isPwcUser
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPageInState,
    currentSortDir,
    currentSortKey,
    projectId,
    filterTags,
    isPwcUser,
    userRole,
  ]);

  const fetchPagedDataForGrid = useCallback(() => {
    return getPagedDataForGrid(
      projectId,
      currentPageInState,
      GRID_PAGE_SIZE,
      filterTags.length ? filterTags : null,
      !isPwcUser
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPageInState, filterTags, projectId, isPwcUser, userRole]);

  const fetchData = async () => {
    setIsFetching(true);
    try {
      const response = viewGrid
        ? await fetchPagedDataForGrid()
        : await fetchPagedDataForList();

      const { items, rowCount, libraryItemGroups, sharedLibraryItems } =
        response;
      const newCompleteGridResponse = {
        ...completeGridResponse,
        libraryItemGroups: {
          excelPptLibraryItemList: [
            ...completeGridResponse.libraryItemGroups.excelPptLibraryItemList,
            ...((libraryItemGroups || {}).excelPptLibraryItemList || []),
          ],
          richTextLibraryItemList: [],
          powerBiLibraryItemList: [],
        },
        items: [...items],
        sharedLibraryItems: [
          ...(completeGridResponse.sharedLibraryItems || []),
          ...(sharedLibraryItems || []),
        ],
      };

      setCompleteGridResponse(newCompleteGridResponse);

      if (rowCount !== currentRowCount) {
        setCurrentRowCount(rowCount);
      }

      const newData =
        currentPageInState > 0
          ? uniqBy(
              [...data, ...newCompleteGridResponse.items],
              'contentThreadId'
            )
          : newCompleteGridResponse.items;

      if (viewGrid) {
        const insightIds = newData
          .filter(
            (x) => x.insight && x.insight.items && x.insight.items.length > 0
          )
          .map(
            (x) => x.insight.items[0].id || x.insight.items[0].libraryItemId
          );

        const preparedItems = await prepareLibraryItems({
          projectId,
          libraryItemGroups: {
            excelPptLibraryItemList: [
              ...((libraryItemGroups || {}).excelPptLibraryItemList || [])
                .filter((x) =>
                  x.excelPptLibraryItems.some((y) => insightIds.includes(y.id))
                )
                .map((x) => ({
                  ...x,
                  excelPptLibraryItems: x.excelPptLibraryItems.filter((y) =>
                    insightIds.includes(y.id)
                  ),
                })),
            ],
            richTextLibraryItemList: [],
            powerBiLibraryItemList: [],
          },
          sections: newData.map((x) => x.insight),
          sharedLibraryItems: [...(sharedLibraryItems || [])],
          contextStatusInfoItem,
          isPwcUser,
        });

        const prepCombined = {
          ...preparedItems,
          excelLibraryItems: [
            ...allPreparedItems.excelLibraryItems,
            ...preparedItems.excelLibraryItems,
          ],
          pptLibraryItems: [
            ...allPreparedItems.pptLibraryItems,
            ...preparedItems.pptLibraryItems,
          ],
          requests: [...allPreparedItems.requests, ...preparedItems.requests],
        };

        const loadedItems = await loadLibraryItems({
          projectId,
          libraryItemGroups: newCompleteGridResponse.libraryItemGroups,
          sections: newData.map((x) => x.insight),
          sharedLibraryItems: newCompleteGridResponse.sharedLibraryItems,
          contextStatusInfoItem,
          ...prepCombined,
        });
        setAllPreparedItems(prepCombined);
        const resultData = newData.map((x, i) => ({
          ...x,
          insight: { ...x.insight, ...loadedItems[i] },
        }));
        setData(resultData);
      } else {
        setData(newData);
      }

      setIsFetching(false);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      setIsFetching(false);
      setIsError(true);
    }
  };

  const fetchMore = () => {
    setCurrentPageInState(currentPageInState + 1);
  };

  const onSort = (sortKey, sortDir) => {
    const prepareForSort = () => {
      setCurrentPageInState(0);
      setData([]);
    };

    if (sortKey && sortKey !== currentSortKey) {
      prepareForSort();
      setCurrentSortKey(sortKey);
    }

    if (sortDir && sortDir !== currentSortDir) {
      prepareForSort();
      setCurrentSortDir(sortDir);
    }
  };

  const hasMore = useCallback(() => {
    if (currentRowCount) {
      return data.length < currentRowCount;
    }
    return true;
  }, [currentRowCount, data]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPageInState,
    filterTags,
    viewGrid,
    currentSortKey,
    currentSortDir,
    isPwcUser,
  ]);

  useEffect(() => {
    setCurrentPageInState(0);
  }, [viewGrid]);

  useEffect(() => {
    if (data.length > 0) {
      if (hasMore() && !canBeScrolled()) {
        fetchMore();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (data.length > 0 && isDeletePerformed) {
      fetchData();
      setIsDeletePerformed(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDeletePerformed, data]);

  const loaderComponent = useCallback(
    () => <ResponsiveLoader dataTestId="Infinite-list-loader" />,
    []
  );

  const endOfListComponent = useCallback(
    () => (
      <InfiniteListMessage
        iconName="confirmed"
        iconClass="outline icon-grey-list-label end-of-infite-list-message__icon"
        message={t('noMoreItemsToDisplay')}
        iconSize={{ width: 24, height: 24 }}
      />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const InfiniteListIndicatorChildren = useMemo(() => {
    if (isFetching) {
      return loaderComponent();
    }
    if (!isFetching && !hasMore()) {
      return endOfListComponent();
    }
    return null;
  }, [isFetching, hasMore, endOfListComponent, loaderComponent]);

  if (isError) {
    return <LoadingError />;
  }

  return (
    <>
      <SyncedInfoProvider value={contextStatusInfoItem}>
        <div className="hypothesis-list-container">
          <HypotyhesesView
            projectId={projectId}
            viewGrid={viewGrid}
            data={data}
            handleToggleView={toggleView}
            handleFilter={onFilter}
            handleDelete={onDelete}
            handleReorder={onReorder}
            handleSort={onSort}
            sortKey={currentSortKey}
            sortDir={currentSortDir}
            fetchMore={fetchMore}
            hasMore={hasMore()}
            isFiltered={Boolean(filterTags.length)}
            currentPage={currentPageInState}
            pageSize={viewGrid ? GRID_PAGE_SIZE : LIST_PAGE_SIZE}
            allRetrievedConnections={allRetrievedConnections}
          />

          {/* Show when no cards for project available. */}
          {!isFetching && data.length === 0 && filterTags.length === 0 && (
            <EmptyHypothesesCard projectId={projectId} />
          )}

          {/* Show when no cards for selected tags available. */}
          {!isFetching && data.length === 0 && filterTags.length > 0 && (
            <NoContentForTagsCard />
          )}
        </div>
      </SyncedInfoProvider>

      <InfiniteListLoadIndicator
        theme={darkMode ? 'dark' : 'light'}
        endOfList={!hasMore()}
      >
        {InfiniteListIndicatorChildren}
      </InfiniteListLoadIndicator>
    </>
  );
};

export default HypothesesListContainer;
